import apiService from "@/core/services/api/api.service";
import { HttpResponse } from "@/core/types/http.types";
import { DEFAULT_HMI } from "../../constants";
import {
  HMIParamsType,
  IHMI,
  IHMIParamError,
  IHMIParamsResponse,
  IHMIParamValue,
  ISetHMIParams,
  IHMIChartPayload,
} from "@/core/models/hmi.model";
import {
  hmiUrls,
  predictionUrls,
  recommendationUrls,
} from "@/core/services/api/urls";
import {
  HMIParamsTransformer,
  predictionHistoryTransformer,
  predictionPayloadTransformer,
  predictionResultTransformer,
  recommendationHistoryTransformer,
  recommendationPayloadTransformer,
  recommendationResultTransformer,
} from "../../transformers/hmi.transformers";
import i18n from "@/core/plugins/i18n";
import { IHMIChart, IPredictionResponse } from "@/core/models/prediction.model";
import {
  IRecommendationPayload,
  IRecommendationResponse,
} from "@/core/models/recommendation.model";
import moment from "jalali-moment";
import { FilterInterface } from "@/core/models/main.model";

// action types
export const GET_HMI_PARAMS = "getHMIParameters";
export const CALCULATE_PREDICTION = "calculatePredictionResults";
export const SAVE_HMI_OPERATION = "saveHMIOperationResults";
export const GET_PREDICTIONS_HISTORY = "getPredictionHistory";
export const GET_LATEST_LAB_DATA = "getLatestLabData";
export const CREATE_HMI_CHART = "createHMIChartData";
export const ADD_RECOMMENDATION = "addRecommendation";
export const GET_RECOMMENDATION_HISTORY = "getRecommendationHistory";

// mutation types
export const SET_HMI_PARAMS_RESPONSE = "setHMIParamsetersResponse";
export const SET_HMI_PARAMS = "setHMIParameters";
export const UPDATE_HMI_PARAM_VALUE = "updateHMIParameterValue";
export const UPDATE_HMI_PARAM_ERROR = "updateHMIParameterError";
export const SET_PREDICTION_RESULTS = "setPredictionResults";
export const RESET_PREDICTION_RESULTS = "resetPredictionResults";
export const SET_PREDICTION_HISTORY = "setPredictionHistoryResults";
export const SET_HMI_CHART = "setHMIChartData";
export const UPDATE_HMI_CHART_CONFIG = "updateHMIChartConfig";
export const SET_RECOMMENDATION_RESULTS = "setRecommendationResults";
export const RESET_RECOMMENDATION_RESULTS = "resetRecommendationResults";
export const SET_RECOMMENDATION_HISTORY = "setRecommendationHistoryResults";

const state: IHMI = DEFAULT_HMI;

// Helper: snap any moment m into your 3-hour buckets (2→2:00, 5→5:00, …, 23→23:00)
function bucketTime(m: any) {
  const h = m.hour();
  // hours since 02:00, wrapped into [0…23]
  const hoursSince2 = (h - 2 + 24) % 24;
  // which 3-hour block [0…7]?
  const block = Math.floor(hoursSince2 / 3);
  // map block back to bucket start hour: 0→2, 1→5, …, 7→23
  const bucketH = (block * 3 + 2) % 24;

  // clone and zero minutes/seconds
  let snapped = m.clone().hour(bucketH).minute(0).second(0).millisecond(0);

  // if we wrapped past midnight (e.g. original was 0 or 1, bucketH is 23),
  // subtract a day so date stays correct
  if (bucketH > h) {
    snapped = snapped.subtract(1, "day");
  }

  return snapped;
}

const getters = {
  HMIParams(state: IHMI) {
    return state.params;
  },
  predictionResult(state: IHMI) {
    return state.predictionResult;
  },
  HMIChartConfig(state: IHMI) {
    return state.HMIChart.config;
  },
  HMIChartData(state: IHMI) {
    return state.HMIChart.data;
  },
  predictionHistory(state: IHMI) {
    return state.predictionHistory;
  },
  recommendationResult(state: IHMI) {
    return state.recommendationResult;
  },
  recommendationHistory(state: IHMI) {
    return state.recommendationHistory;
  },
};

const actions = {
  async [GET_HMI_PARAMS](context: any, checkEnable?: boolean) {
    try {
      const response = await apiService.request({
        request: {
          url: hmiUrls.getParameters,
          method: "GET",
        },
      });
      const { data } = response;
      context.commit(SET_HMI_PARAMS_RESPONSE, data.data);
      context.commit(SET_HMI_PARAMS, { type: "default", checkEnable });
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [CALCULATE_PREDICTION](context: any, interval: number) {
    const paramsData = context.rootState.HMI.params as HMIParamsType;
    const validationCheck = Object.values(paramsData).every(
      (param) => !param.error
    );
    if (!validationCheck) {
      throw { message: i18n.t("HMIError") };
    }
    try {
      const response = await apiService.request({
        request: {
          url: predictionUrls.calculate,
          method: "POST",
          data: predictionPayloadTransformer(paramsData, interval),
        },
      });
      const { data } = response;
      context.commit(SET_PREDICTION_RESULTS, data.data);
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [GET_PREDICTIONS_HISTORY](context: any, filterData?: FilterInterface) {
    try {
      const response = await apiService.request({
        request: {
          url: predictionUrls.history,
          method: "GET",
          data: filterData,
        },
      });
      const { data } = response;
      context.commit(SET_PREDICTION_HISTORY, data?.data || []);
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [ADD_RECOMMENDATION](context: any, payload: IRecommendationPayload) {
    const paramsData = context.rootState.HMI.params as HMIParamsType;
    const validationCheck = Object.values(paramsData).every(
      (param) => !param.error
    );
    if (!validationCheck) {
      throw { message: i18n.t("HMIError") };
    }
    try {
      const response = await apiService.request({
        request: {
          url: recommendationUrls.add,
          method: "POST",
          data: recommendationPayloadTransformer(paramsData, payload),
        },
      });
      const { data } = response;
      context.commit(SET_RECOMMENDATION_RESULTS, data.data);
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [GET_RECOMMENDATION_HISTORY](
    context: any,
    filterData?: FilterInterface
  ) {
    try {
      const response = await apiService.request({
        request: {
          url: recommendationUrls.history,
          method: "GET",
          data: filterData,
        },
      });
      const { data } = response;
      context.commit(SET_RECOMMENDATION_HISTORY, data?.data || []);
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [SAVE_HMI_OPERATION](context: any, operationId?: number) {
    if (operationId) {
      try {
        const response = await apiService.request({
          request: {
            url: hmiUrls.saveOperation(operationId),
            method: "POST",
          },
        });
        context.commit(RESET_PREDICTION_RESULTS);
        context.commit(RESET_RECOMMENDATION_RESULTS);
        context.dispatch(CREATE_HMI_CHART);
        context.dispatch(GET_HMI_PARAMS);
        return response;
      } catch (error: any) {
        throw { ...error } as HttpResponse;
      }
    }
    return;
  },
  async [GET_LATEST_LAB_DATA](_context: any, filterData?: FilterInterface) {
    try {
      const response = await apiService.request({
        request: {
          url: hmiUrls.getLatestLabParameters,
          method: "GET",
          data: filterData,
        },
      });
      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [CREATE_HMI_CHART](
    context: any,
    type: "recommendation" | "prediction"
  ) {
    const { from, to } = context.rootState.HMI.HMIChart
      .config as IHMIChart["config"];
    const filterDarta: FilterInterface = {
      created_at_from: moment(from).format("YYYY-MM-DD"),
      created_at_to: moment(to).format("YYYY-MM-DD"),
    };
    try {
      const {
        data: { data: HMIData },
      } = await context.dispatch(
        type === "recommendation"
          ? GET_RECOMMENDATION_HISTORY
          : GET_PREDICTIONS_HISTORY,
        filterDarta
      );
      const {
        data: { data: labData },
      } = await context.dispatch(GET_LATEST_LAB_DATA, filterDarta);
      context.commit(SET_HMI_CHART, { HMIData, labData });
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
};

const mutations = {
  [SET_HMI_PARAMS_RESPONSE](state: IHMI, response: IHMIParamsResponse[]) {
    state.paramsResponse = response;
  },
  [SET_HMI_PARAMS](state: IHMI, config: ISetHMIParams) {
    const response = state.paramsResponse;
    state.params = HMIParamsTransformer(response, config);
  },
  [UPDATE_HMI_PARAM_VALUE](state: IHMI, { key, value }: IHMIParamValue) {
    state.params = {
      ...state.params,
      [key]: {
        ...state.params[key],
        value,
      },
    };
  },
  [UPDATE_HMI_PARAM_ERROR](state: IHMI, { key, error }: IHMIParamError) {
    state.params = {
      ...state.params,
      [key]: {
        ...state.params[key],
        error,
      },
    };
  },
  [SET_PREDICTION_RESULTS](state: IHMI, response: IPredictionResponse) {
    state.predictionResult = predictionResultTransformer(response);
  },
  [RESET_PREDICTION_RESULTS](state: IHMI) {
    state.predictionResult = {};
  },
  [RESET_RECOMMENDATION_RESULTS](state: IHMI) {
    state.recommendationResult = {};
  },
  [UPDATE_HMI_CHART_CONFIG](state: IHMI, data: Partial<IHMIChart["config"]>) {
    state.HMIChart.config = {
      ...state.HMIChart.config,
      ...data,
    };
  },
  [SET_HMI_CHART](state: IHMI, { HMIData, labData }: IHMIChartPayload) {
    const chartData: IHMIChart["data"] = {
      3: [],
      5: [],
      8: [],
      lab: [],
    };
    if (HMIData) {
      HMIData.forEach((data) => {
        const time = moment(new Date(data.created_at))
          .add(data.interval, "hour")
          .valueOf();
        const chartKey = data.interval as 3 | 5 | 8;
        const param = state.HMIChart.config.parameter;
        chartData[chartKey].push([time, data[param]]);
      });
    }
    if (labData) {
      chartData.lab = labData.map((data) => {
        // parse your UTC timestamp into a local-time moment
        const m = moment(new Date(data.created_at));
        // snap it into your bucket
        const snapped = bucketTime(m);

        // return [ms-since-epoch, value]
        return [snapped.valueOf(), data[state.HMIChart.config.parameter]];
      });
    }
    state.HMIChart.data = chartData;
  },
  [SET_PREDICTION_HISTORY](state: IHMI, response: IPredictionResponse[]) {
    state.predictionHistory = predictionHistoryTransformer(response);
  },
  [SET_RECOMMENDATION_RESULTS](state: IHMI, response: IRecommendationResponse) {
    state.recommendationResult = recommendationResultTransformer(response);
  },
  [SET_RECOMMENDATION_HISTORY](
    state: IHMI,
    response: IRecommendationResponse[]
  ) {
    state.recommendationHistory = recommendationHistoryTransformer(response);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
